/* eslint-disable camelcase */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import {
  checkoutApi,
  getCheckoutMetaApi,
  getCheckoutPaymentMethodsApi,
  getListOfVouchersApi,
  getStoreOrderMetaDataApi,
  removeSelectedVoucherApi,
  RequestType,
  updateCheckoutMetaApi,
  updateDeliveryDetailsApi,
  validateVoucherApi
} from 'src/services/oms'
import {
  mockCheckoutMeta,
  mockCheckoutResult,
  mockPaymentOptions
} from 'src/services/mock'
import { createValidationPayload } from 'src/utils/voucher'
import type { CustomisationStateInReducer } from '../customisation'
import type { CheckoutStateInReducer } from './slice'
import { isObjectEmpty } from 'src/utils/object'

export interface PayCheckoutParams {
  storeOrderId: string
  customerId: string
  customerEmail: string
  customerFirstName: string
  customerLastName: string
  customerContactNumber: string
  customerCompany: string
  recipientName: string
  recipientContact: string
  billingAddressOne: string
  billingAddressTwo: string
  billingAddressState: string
  billingAddressCity: string
  billingAddressPostal: string
  billingAddressCountry: string
  stripeRequestPaymentButtonType: string
  additionalInformation: string
  paymentOption: string
  paymentOptionId: string
  paymentType: number
  subscribeToEmail: boolean
  unifiedBusinessNumber: string
  carrierType: string
  carrierNumber: string
  paymentToken: string
  redirectUrl: string
  otp: string
  version: string
  voucher?: {
    code: string
    voucherValidation: {
      customer?: {
        source_id: string
        email: string
      }
      order?: {
        source_id: string
        amount: number
        items: {
          product_id: string
          quantity: number
          price: number
        }[]
        metadata: {
          menuId: string
          isPickup: boolean
          oddlePassFirstOrder: boolean
          oddlePassFirstMenuOrder: boolean
          fulfilmentDate: string
          submissionDate: string
          cardBin: string
        }
      }
      session: string
    }
  }
  saveForFuture?: boolean
  existing?: boolean
  organisationName?: string
}

export const fetchCheckoutMetaAction = createAsyncThunk(
  '@checkout/fetchCheckoutMeta',
  async (
    {
      req,
      storeOrderId,
      isServer
    }: { req?: RequestType; storeOrderId: string; isServer?: boolean },
    thunkApi
  ) => {
    if (!storeOrderId) {
      return thunkApi.rejectWithValue('storeOrderId is required')
    }
    if (storeOrderId === 'preview') {
      return {
        data: mockCheckoutMeta
      }
    }
    try {
      const result = await getCheckoutMetaApi({ req, storeOrderId, isServer })
      if (isObjectEmpty(result?.data?.data)) {
        throw result
      }
      const meta = result.data?.data
      if (meta) {
        const { orderVouchers } = meta.storeOrder || {}
        const orderVoucher = (orderVouchers || [])[0]
        thunkApi.dispatch(setSelectedVoucherAction(orderVoucher))
      }
      return result.data
    } catch (err) {
      console.log(err)
      return thunkApi.rejectWithValue(err.response.data)
    }
  }
)

export const updateCheckoutMetaAction = createAsyncThunk(
  '@checkout/updateCheckoutMeta',
  async ({
    req,
    storeOrderId,
    payload
  }: {
    req?: RequestType
    storeOrderId: string | string[]
    payload: Record<string, unknown>
  }) => {
    const result = await updateCheckoutMetaApi({
      req,
      storeOrderId,
      payload
    })
    return result.data
  }
)

export const fetchCheckoutPaymentMethodsAction = createAsyncThunk(
  '@checkout/fetchCheckoutPaymentMethods',
  async ({
    req,
    storeOrderId,
    isServer
  }: {
    req?: RequestType
    storeOrderId: string
    isServer?: boolean
  }) => {
    if (storeOrderId === 'preview') {
      return mockPaymentOptions
    }
    const result = await getCheckoutPaymentMethodsApi({
      req,
      storeOrderId,
      isServer
    })
    return result.data
  }
)

export const checkoutAction = createAsyncThunk(
  '@checkout/checkout',
  async ({ payload }: { payload: PayCheckoutParams }, thunkApi) => {
    const state = thunkApi.getState() as CustomisationStateInReducer
    const previewMode = state['@customisation'].data.previewMode
    if (previewMode) {
      return mockCheckoutResult
    }
    try {
      const result = await checkoutApi(payload)
      if (result.data) return result.data
      const { response } = { ...result } as {
        response?: {
          data: {
            data
            errors
          }
        }
      }
      return response.data
    } catch (err) {
      console.log(err)
      return thunkApi.rejectWithValue(err.response.data)
    }
  }
)

export const updateDeliveryDetailsAction = createAsyncThunk(
  '@checkout/updateDeliveryDetails',
  async (
    {
      storeOrderId,
      deliveryAddressType,
      deliveryAddressTwoDetails,
      deliveryInstructions
    }: {
      storeOrderId: string
      deliveryAddressType: string
      deliveryAddressTwoDetails
      deliveryInstructions
    },
    thunkApi
  ) => {
    const state = thunkApi.getState() as CustomisationStateInReducer
    const previewMode = state['@customisation'].data.previewMode
    if (previewMode) {
      return mockCheckoutResult
    }
    const result = await updateDeliveryDetailsApi({
      storeOrderId,
      deliveryAddressType,
      deliveryAddressTwoDetails,
      deliveryInstructions
    })
    return result.data
  }
)

export const getStoreOrderMetaDataAction = createAsyncThunk(
  '@checkout/getStoreOrderMetaData',
  async ({ storeOrderId }: { storeOrderId: string }) => {
    const result = await getStoreOrderMetaDataApi({
      storeOrderId
    })
    return result.data
  }
)

export const getListOfVouchersAction = createAsyncThunk(
  '@checkout/getListOfVouchers',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as CheckoutStateInReducer
    try {
      const result = await getListOfVouchersApi(
        createValidationPayload(state['@checkout'].meta.data, false)
      )
      return result.data
    } catch (err) {
      console.log('getListOfVouchers', err.response)
      return thunkApi.rejectWithValue(err.response?.data || err)
    }
  }
)

export const validateVoucherAction = createAsyncThunk(
  '@checkout/validateVoucher',
  async ({ code }: { code: string }, thunkApi) => {
    const state = thunkApi.getState() as CheckoutStateInReducer
    try {
      const result = await validateVoucherApi({
        ...createValidationPayload(state['@checkout'].meta.data, false),
        code
      })
      return result.data
    } catch (err) {
      return thunkApi.rejectWithValue(err.response.data)
    }
  }
)

export const setSelectedVoucherAction = createAction(
  '@checkout/setSelectedVoucher',
  (selectedVoucher?: Oms.V4.SelectedVoucher) => {
    return {
      payload: selectedVoucher
    }
  }
)

export const removeSelectedVoucherAction = createAsyncThunk(
  '@checkout/removeSelectedVoucher',
  async (params, thunkApi) => {
    const state = thunkApi.getState() as CheckoutStateInReducer
    const { id, orderVouchers } = state['@checkout'].meta.data.storeOrder
    if (!orderVouchers?.[0]?.id || !id) {
      return true
    }
    const result = await removeSelectedVoucherApi({
      storeOrderId: id,
      voucherId: orderVouchers[0].id
    })
    return result.data.data
  }
)

export const storeCheckoutAddressAction = createAction(
  '@checkout/storeCheckoutAddress',
  (data: {
    storeOrderId: string
    organisationName: string
    recipientCheckbox: string
    recipientName?: string
    recipientContact?: string
    billingAddressDifferentCheckbox: boolean
    additionalRemarks: string
  }) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('cachedCheckoutAddress', JSON.stringify(data))
    }
    return {
      payload: data
    }
  }
)

export const retrieveCheckoutAddressAction = createAction(
  '@checkout/retrieveCheckoutAddress',
  (storeOrderId: string) => {
    if (typeof window !== 'undefined') {
      const storeCheckoutAddress = window.localStorage.getItem(
        'cachedCheckoutAddress'
      )
      if (storeCheckoutAddress) {
        const parsedData = JSON.parse(storeCheckoutAddress)
        if (parsedData.storeOrderId === storeOrderId) {
          return {
            payload: parsedData
          }
        }
      }
    }
    return {
      payload: null
    }
  }
)

export const selectSavedCreditCardAction = createAction(
  '@checkout/selectSavedCreditCard',
  (data: Oms.V4.SelectedSavedCreditCard) => {
    return {
      payload: data
    }
  }
)

export const unselectSavedCreditCardAction = createAction(
  '@checkout/unselectSavedCreditCard',
  () => {
    return { payload: true }
  }
)
